import {FC, useState} from "react";
import {Icon} from "semantic-ui-react";
import {IInsightAction, InsightActionStatus} from "../api/coachInsightsModels";
import ReviewOutcomeModal from "./ReviewOutcomeModal";

interface IProps {
    actions: IInsightAction[];
    onActionsChanged: () => void;
}

function formatRoi(roi: number): string {
    if (roi >= 1000) return `$${(roi / 1000).toFixed(1)}k`;
    return `$${roi}`;
}

function getDaysInfo(createdAt: string): {label: string; className: string} {
    const WINDOW_DAYS = 7;
    const diffDays = Math.floor(
        (Date.now() - new Date(createdAt).getTime()) / 86_400_000
    );
    const remaining = WINDOW_DAYS - diffDays;
    if (remaining > 0) {
        return {label: `${remaining} day${remaining !== 1 ? "s" : ""} left`, className: "days-left"};
    }
    const overdue = Math.abs(remaining);
    return {label: `${overdue} day${overdue !== 1 ? "s" : ""} overdue`, className: "days-overdue"};
}

const InsightFollowUpPanel: FC<IProps> = ({actions, onActionsChanged}) => {
    const [reviewingAction, setReviewingAction] = useState<IInsightAction | null>(null);
    const [showCompleted, setShowCompleted] = useState(true);

    const completed = actions.filter(
        (a) => a.status === InsightActionStatus.Resolved || a.status === InsightActionStatus.NotImproved
    );
    const open = actions.filter(
        (a) => a.status === InsightActionStatus.Open || a.status === InsightActionStatus.InProgress
    );
    const totalRoi = completed.reduce((sum, a) => sum + (a.estimatedRoi ?? 0), 0);

    return (
        <>
            <div className="sidebar-section">
                <div className="sidebar-section-header">
                    <div className="sidebar-section-title-row">
                        <h3 className="sidebar-section-title">Tasks Completed</h3>
                        {totalRoi > 0 && (
                            <span className="sidebar-roi-ytd">{formatRoi(totalRoi)} YTD</span>
                        )}
                    </div>
                    <p className="sidebar-section-subtitle">
                        Results from completed tasks across your locations
                    </p>
                </div>

                {completed.length === 0 ? (
                    <div className="sidebar-empty">No completed tasks yet.</div>
                ) : (
                    <div className="sidebar-task-list">
                        {completed.map((action) => (
                            <div key={action.id} className="sidebar-task-row sidebar-task-completed">
                                <Icon name="check circle" className="sidebar-task-icon icon-completed" />
                                <span className="sidebar-task-title">{action.title}</span>
                                <span className="sidebar-task-roi-saved">
                                    <Icon name="dollar sign" />
                                    {formatRoi(action.estimatedRoi)}{" "}
                                    {action.reviewOutcome === "Improved" ? "saved" : "est."}
                                </span>
                                <Icon name="chevron down" className="sidebar-task-chevron" />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="sidebar-divider" />

            <div className="sidebar-section">
                <div className="sidebar-section-header">
                    <div className="sidebar-section-title-row">
                        <h3 className="sidebar-section-title">Follow Up Tasks</h3>
                        <div className="sidebar-followup-controls">
                            {actions.length > 0 && (
                                <span className="sidebar-followup-count">
                                    <Icon name="check circle outline" />
                                    {completed.length}/{actions.length}
                                </span>
                            )}
                            <button
                                className={`sidebar-toggle${showCompleted ? " on" : ""}`}
                                onClick={() => setShowCompleted(!showCompleted)}
                                title={showCompleted ? "Hide completed" : "Show completed"}
                            >
                                <span className="sidebar-toggle-knob" />
                            </button>
                        </div>
                    </div>
                    <p className="sidebar-section-subtitle">
                        Actions you need to take to ensure success
                    </p>
                </div>

                {actions.length === 0 ? (
                    <div className="sidebar-empty">
                        Record an action on an insight above to track it here.
                    </div>
                ) : (
                    <div className="sidebar-task-list">
                        {open.map((action) => {
                            const days = getDaysInfo(action.createdAt);
                            const assignee = action.whoToFollowUpWith?.[0] ?? null;
                            return (
                                <div
                                    key={action.id}
                                    className="sidebar-task-row sidebar-task-open"
                                    onClick={() => setReviewingAction(action)}
                                >
                                    <Icon
                                        name="exclamation circle"
                                        className={`sidebar-task-icon ${days.className === "days-overdue" ? "icon-overdue" : "icon-pending"}`}
                                    />
                                    <div className="sidebar-task-body">
                                        <span className="sidebar-task-title">{action.title}</span>
                                        {assignee && (
                                            <span className="sidebar-task-assignee">
                                                <Icon name="user outline" />
                                                {assignee}
                                            </span>
                                        )}
                                    </div>
                                    {action.estimatedRoi > 0 && (
                                        <span className="sidebar-task-roi-open">
                                            {formatRoi(action.estimatedRoi)}
                                        </span>
                                    )}
                                    <span className={`sidebar-task-days ${days.className}`}>
                                        {days.label}
                                    </span>
                                    <Icon name="chevron down" className="sidebar-task-chevron" />
                                </div>
                            );
                        })}

                        {showCompleted &&
                            completed.map((action) => (
                                <div key={action.id} className="sidebar-task-row sidebar-task-done">
                                    <Icon name="check circle" className="sidebar-task-icon icon-completed" />
                                    <div className="sidebar-task-body">
                                        <span className="sidebar-task-title done-title">
                                            {action.title}
                                        </span>
                                        {action.whoToFollowUpWith?.[0] && (
                                            <span className="sidebar-task-assignee">
                                                <Icon name="user outline" />
                                                {action.whoToFollowUpWith[0]}
                                            </span>
                                        )}
                                    </div>
                                    <Icon name="chevron down" className="sidebar-task-chevron" />
                                </div>
                            ))}
                    </div>
                )}
            </div>

            {reviewingAction && (
                <ReviewOutcomeModal
                    action={reviewingAction}
                    onClose={() => setReviewingAction(null)}
                    onResolved={onActionsChanged}
                />
            )}
        </>
    );
};

export default InsightFollowUpPanel;
